// src/api/push.ts
/**
 * Web Push: подписки браузера на уведомления.
 *
 * Контракт бэкенда (docs/api-versioning.md, R2 — версия в пути):
 *   GET    /api/v1/push/vapid-public-key  → 200 { "publicKey": "<base64url>" }
 *                                           503 { "error": "Web Push не настроен на сервере" }
 *   POST   /api/v1/push/subscriptions     { endpoint, keys: { p256dh, auth } } → 201
 *   DELETE /api/v1/push/subscriptions     { endpoint }                          → 200
 *
 * axiosClient уже подставляет baseURL `/api`, поэтому здесь пути начинаются с `/v1`.
 */
import axiosClient from './axiosClient';

/** Ответ сервера с публичным ключом VAPID. */
export interface VapidPublicKeyResponse {
    publicKey?: string | null;
}

/**
 * Состояние Web Push на сервере:
 *  - `ok`       — ключ получен, можно подписываться;
 *  - `disabled` — сервер ответил ошибкой (обычно 503): push не настроен;
 *  - `error`    — сервер недоступен (сеть/таймаут), это не «выключено».
 */
export type VapidKeyStatus = 'ok' | 'disabled' | 'error';

export interface VapidKeyResult {
    status: VapidKeyStatus;
    publicKey?: string;
}

/** Тело подписки, которое ждёт бэкенд. */
export interface PushSubscriptionPayload {
    endpoint: string;
    keys: {
        p256dh: string;
        auth: string;
    };
}

const httpStatus = (error: unknown): number | undefined => {
    if (typeof error !== 'object' || error === null) return undefined;
    const response = (error as { response?: { status?: unknown } }).response;
    return typeof response?.status === 'number' ? response.status : undefined;
};

/**
 * Публичный VAPID-ключ для подписки.
 *
 * Не бросает исключение: «push выключен на сервере» — рядовое состояние,
 * а не сбой, и настройки должны показать подсказку, а не упасть.
 */
export const fetchVapidPublicKey = async (): Promise<VapidKeyResult> => {
    try {
        const { data } = await axiosClient.get<VapidPublicKeyResponse>('/v1/push/vapid-public-key');
        const publicKey = typeof data?.publicKey === 'string' ? data.publicKey.trim() : '';
        if (!publicKey) {
            return { status: 'disabled' };
        }
        return { status: 'ok', publicKey };
    } catch (error) {
        // Любой ответ сервера (503 при не настроенном Web Push, 500 и т.п.)
        // означает «push сейчас недоступен», а вот отсутствие ответа —
        // сетевой сбой, его стоит показать отдельно.
        if (httpStatus(error) !== undefined) {
            return { status: 'disabled' };
        }
        return { status: 'error' };
    }
};

/** Сообщить серверу о подписке. 201 — подписка сохранена. */
export const registerPushSubscription = async (
    payload: PushSubscriptionPayload
): Promise<void> => {
    await axiosClient.post('/v1/push/subscriptions', payload);
};

/** Снять подписку на сервере (локально её снимает браузер). */
export const unregisterPushSubscription = async (endpoint: string): Promise<void> => {
    await axiosClient.delete('/v1/push/subscriptions', { data: { endpoint } });
};

/**
 * Подписка устарела: push-служба её отозвала (404/410). Бэкенд может ответить
 * так же, если подписки уже нет, — в этом случае её надо пересоздать.
 */
export const isStaleSubscriptionError = (error: unknown): boolean => {
    const status = httpStatus(error);
    return status === 404 || status === 410;
};
